import { cn } from "lib/utils";
import { type ChangeEvent, type FC, useRef, useState } from "react";

export type FromToNumberInputProps = {
  min: number;
  max: number;
  step?: number;
  fromValue?: number;
  toValue?: number;
  onFromChange?: (value: number) => void;
  onToChange?: (value: number) => void;
  unitName?: string;
  /** プルダウン候補。未指定なら min〜max を step 刻みで生成 */
  presets?: number[];
  testLabel?: string;
};

type NumberComboProps = {
  value?: number;
  onChange?: (v: number) => void;
  presets: number[];
  testId?: string;
};

const NumberCombo: FC<NumberComboProps> = ({
  value,
  onChange,
  presets,
  testId,
}) => {
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);

  const handleInput = (e: ChangeEvent<HTMLInputElement>) => {
    const n = Number(e.target.value);
    if (!Number.isNaN(n)) onChange?.(n);
  };

  const handleSelect = (v: number) => {
    onChange?.(v);
    setOpen(false);
  };

  const handleBlur = (e: React.FocusEvent) => {
    if (!wrapperRef.current?.contains(e.relatedTarget as Node)) {
      setOpen(false);
    }
  };

  return (
    <div ref={wrapperRef} className="relative" onBlur={handleBlur}>
      <div className="input input-bordered input-sm flex items-center w-24 pr-0">
        <input
          data-testid={testId}
          type="number"
          className={cn(
            "w-full bg-transparent outline-none",
            "[&::-webkit-inner-spin-button]:appearance-none",
            "[&::-webkit-outer-spin-button]:appearance-none",
            "[appearance:textfield]",
          )}
          value={value ?? ""}
          onChange={handleInput}
          onFocus={() => setOpen(true)}
        />
        <button
          type="button"
          className="shrink-0 pl-1 pr-2 opacity-50 hover:opacity-100"
          tabIndex={0}
          onClick={() => setOpen(prev => !prev)}
        >
          <svg
            className={cn("h-3 w-3 transition-transform", open && "rotate-180")}
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M19 9l-7 7-7-7"
            />
          </svg>
        </button>
      </div>
      {open && (
        <ul className="absolute z-50 mt-1 max-h-60 w-full overflow-y-auto rounded-md border border-base-300 bg-base-100 shadow-lg">
          {presets.map(v => (
            <li key={v}>
              <button
                type="button"
                className={cn(
                  "w-full px-3 py-1 text-left text-sm hover:bg-base-200",
                  v === value && "bg-primary text-white font-semibold",
                )}
                onMouseDown={e => e.preventDefault()}
                onClick={() => handleSelect(v)}
              >
                {v}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

/**
 * @param {FromToNumberInputProps} props props
 * @return {JSX.Element} from/to 入力
 */
export const FromToNumberInput = ({
  min,
  max,
  step = 1,
  fromValue,
  toValue,
  onFromChange,
  onToChange,
  unitName = "",
  presets,
  testLabel = "",
}: FromToNumberInputProps): JSX.Element => {
  const effectivePresets =
    presets ??
    (() => {
      const out: number[] = [];
      for (let v = min; v <= max; v += step) out.push(v);
      return out;
    })();

  return (
    <div className="flex items-center gap-2">
      <NumberCombo
        testId={testLabel ? `${testLabel}-from` : undefined}
        value={fromValue}
        onChange={onFromChange}
        presets={effectivePresets}
      />
      <span className="text-base-content/60 shrink-0">〜</span>
      <NumberCombo
        testId={testLabel ? `${testLabel}-to` : undefined}
        value={toValue}
        onChange={onToChange}
        presets={effectivePresets}
      />
      {unitName && (
        <span className="text-xs text-base-content/70 shrink-0">
          {unitName}
        </span>
      )}
    </div>
  );
};
